import { integrationDataSourceSchema } from "./integration-data-policy";
import { productionSchema, pdfArtBoxSchema } from "./production";
import { vectorPathError } from "./vector-path";
import { bytesFromBase64 } from "./binary";
import { impositionSchema } from "./imposition";
import { z } from "zod";
import {
  validationSettingsSchema,
  formatSettingsSchema,
} from "./field-policies";
import { copyfitSchema } from "./copyfit";
import { printProfileSchema } from "./print-profile";
import { presetDependencies } from "./presets";
import { fontFaceSchema, bytesHash, textFeaturesSchema } from "./fonts";
import { mapSettingsSchema } from "./maps";
import { statementOptions } from "./statements";
import { textTemplateSchema } from "./text-template";
import { importedPaintSchema } from "./imported-paint";
import { assertLayoutGroups } from "./layout-groups";
import {
  createCalculationSchema,
  calculationDependencies,
  calculationConditions,
} from "./calculations";

export const fieldTypes = [
  "text",
  "textarea",
  "richtext",
  "checkbox",
  "dropdown",
  "combobox",
  "radio",
  "image",
  "image-list",
  "date",
  "datetime",
  "time",
  "phone",
  "email",
  "zip",
  "uri",
  "qr",
  "address",
  "map",
  "preset",
  "number",
  "calendar",
  "color",
] as const;
export const imageTools = [
  "crop",
  "rotate",
  "flipX",
  "flipY",
  "grayscale",
  "invert",
  "brightness",
  "contrast",
  "exposure",
  "saturation",
  "hue",
  "temperature",
  "tint",
  "sharpness",
  "blur",
  "opacity",
  "resize",
  "removeBackground",
] as const;
export const choiceSchema = z.object({
  assetId: z
    .string()
    .regex(/^asset_[a-zA-Z0-9_-]+$/)
    .optional(),
  value: z.string(),
  label: z.string(),
  image: z.string().optional(),
});
export const datasetSchema = z.object({
  id: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  version: z.number().int().positive().default(1),
  kind: z.enum(["choices", "table"]).default("choices"),
  choices: z.array(choiceSchema).max(10000).default([]),
  sort: z
    .object({
      property: z.string().min(1).max(100),
      direction: z.enum(["asc", "desc"]),
    })
    .strict()
    .optional(),
  columns: z
    .array(
      z.object({
        key: z.string().min(1).max(100),
        label: z.string().min(1).max(200),
        type: z.enum(["string", "number", "boolean"]).default("string"),
        multiline: z.boolean().optional(),
      }),
    )
    .max(50)
    .default([]),
  rows: z
    .array(
      z.record(
        z.string(),
        z.union([z.string().max(32768), z.number(), z.boolean(), z.null()]),
      ),
    )
    .max(10000)
    .default([]),
  primaryKey: z.string().optional(),
  valueColumn: z.string().optional(),
  labelColumn: z.string().optional(),
  imageColumn: z.string().optional(),
  assetColumn: z.string().optional(),
});
export type Dataset = z.infer<typeof datasetSchema>;
/** Stable ordering is part of the saved payload, not just an editor view. */
export function orderedDataset(dataset: Dataset): Dataset {
  if (!dataset.sort) return dataset;
  const { property, direction } = dataset.sort;
  const allowed =
    dataset.kind === "choices"
      ? ["value", "label"]
      : dataset.columns.map((column) => column.key);
  if (!allowed.includes(property))
    throw new Error("Dataset sort property is unavailable");
  const compare = (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const av = a[property],
      bv = b[property];
    if (av == null || bv == null) return av == null ? (bv == null ? 0 : 1) : -1;
    const result =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : typeof av === "boolean" && typeof bv === "boolean"
          ? Number(av) - Number(bv)
          : String(av).localeCompare(String(bv), "en", {
              numeric: true,
              sensitivity: "base",
            });
    return direction === "desc" ? -result : result;
  };
  return dataset.kind === "choices"
    ? { ...dataset, choices: [...dataset.choices].sort(compare) }
    : { ...dataset, rows: [...dataset.rows].sort(compare) };
}
export function assertDataset(d: Dataset): void {
  if (new TextEncoder().encode(JSON.stringify(d)).length > 1000000)
    throw new Error("Dataset exceeds 1 MB");
  if (d.kind === "choices") {
    if (new Set(d.choices.map((c) => c.value)).size !== d.choices.length)
      throw new Error("Dataset values must be unique");
    return;
  }
  const columns = new Map(d.columns.map((c) => [c.key, c]));
  if (!d.columns.length || columns.size !== d.columns.length)
    throw new Error("Table columns must be nonempty and unique");
  if (!d.primaryKey || !columns.has(d.primaryKey))
    throw new Error("Table primary key must name a column");
  for (const key of [
    d.valueColumn,
    d.labelColumn,
    d.imageColumn,
    d.assetColumn,
  ])
    if (key && !columns.has(key))
      throw new Error(`Unknown dataset column: ${key}`);
  const keys = new Set<string>();
  for (const row of d.rows) {
    for (const [key, value] of Object.entries(row)) {
      const column = columns.get(key);
      if (!column) throw new Error(`Unknown table row column: ${key}`);
      if (value !== null && typeof value !== column.type)
        throw new Error(`Table cell type mismatch: ${key}`);
    }
    const key = row[d.primaryKey];
    if (key === null || key === undefined || String(key).trim() === "")
      throw new Error("Table primary key is required in every row");
    if (keys.has(String(key)))
      throw new Error("Table primary keys must be unique");
    if (
      d.assetColumn &&
      row[d.assetColumn] !== null &&
      row[d.assetColumn] !== undefined &&
      row[d.assetColumn] !== "" &&
      !/^asset_[a-zA-Z0-9_-]+$/.test(String(row[d.assetColumn]))
    )
      throw new Error("Table image assets must use managed asset IDs");
    keys.add(String(key));
  }
}
export type Choice = z.infer<typeof choiceSchema>;
export type Value =
  string | number | boolean | string[] | Record<string, unknown> | null;
export type Values = Record<string, Value>;
export const matchOperatorSchema = z.enum([
  "eq",
  "startsWith",
  "endsWith",
  "contains",
  "matches",
]);
export interface Condition {
  field?: string;
  op?:
    | "eq"
    | "neq"
    | "empty"
    | "notEmpty"
    | "startsWith"
    | "endsWith"
    | "contains"
    | "notContains"
    | "matches"
    | "gt"
    | "lt"
    | "in";
  value?: Value;
  all?: Condition[];
  any?: Condition[];
}
const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z
    .object({
      field: z.string().optional(),
      op: z
        .enum([
          "eq",
          "neq",
          "empty",
          "notEmpty",
          "startsWith",
          "endsWith",
          "contains",
          "notContains",
          "matches",
          "gt",
          "lt",
          "in",
        ])
        .optional(),
      value: z.any().optional(),
      all: z.array(conditionSchema).max(20).optional(),
      any: z.array(conditionSchema).max(20).optional(),
    })
    .strict(),
);
export const adjustmentSchema = z
  .object({
    version: z.literal(1).default(1),
    crop: z
      .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().positive().max(1),
        height: z.number().positive().max(1),
      })
      .default({ x: 0, y: 0, width: 1, height: 1 }),
    rotate: z.number().min(-180).max(180).default(0),
    flipX: z.boolean().default(false),
    flipY: z.boolean().default(false),
    grayscale: z.boolean().default(false),
    invert: z.boolean().default(false),
    brightness: z.number().min(-1).max(1).default(0),
    contrast: z.number().min(-1).max(1).default(0),
    exposure: z.number().min(-5).max(5).default(0),
    saturation: z.number().min(0).max(2).default(1),
    hue: z.number().min(-180).max(180).default(0),
    temperature: z.number().min(-100).max(100).default(0),
    tint: z.number().min(-100).max(100).default(0),
    sharpness: z.number().min(0).max(10).default(0),
    blur: z.number().min(0).max(100).default(0),
    opacity: z.number().min(0).max(1).default(1),
    width: z.number().int().positive().max(12000).optional(),
    removeBackground: z.boolean().default(false),
    backgroundMaskAssetId: z
      .string()
      .max(200)
      .regex(/^asset_[a-zA-Z0-9_-]+$/)
      .optional(),
  })
  .strict()
  .refine(
    (a) =>
      a.crop.x + a.crop.width <= 1.000001 &&
      a.crop.y + a.crop.height <= 1.000001,
    "Crop exceeds source bounds",
  );
export type Adjustments = z.infer<typeof adjustmentSchema>;
export const defaultAdjustments = (): Adjustments => adjustmentSchema.parse({});
export const formProfileSourceSchema = z
  .object({
    id: z.string().min(1).max(200),
    version: z.number().int().positive(),
    mapping: z
      .record(z.string().min(1).max(200), z.string().min(1).max(200))
      .refine((value) => Object.keys(value).length <= 100),
  })
  .strict();
export const fieldSchema = z.object({
  calculation: createCalculationSchema(conditionSchema).nullable().optional(),
  id: z.string().min(1),
  key: z.string(),
  label: z.string(),
  type: z.enum(fieldTypes),
  order: z.number().default(0),
  groupId: z.string().optional(),
  help: z.string().default(""),
  required: z.boolean().default(false),
  hidden: z.boolean().default(false),
  // Form presentation is independent of document/rule visibility and value access.
  formDisplay: z.enum(["show", "hide", "remove"]).optional(),
  editable: z.boolean().default(true),
  system: z.boolean().default(false),
  default: z.any().default(""),
  source: z
    .object({
      kind: z.enum([
        "literal",
        "user",
        "order",
        "dataset",
        "lookup",
        "form",
        "asset",
        "dam",
        "image-rule",
        "dynamic",
        "statement",
      ]),
      dynamicMode: z.enum(["value", "choices"]).optional(),
      dynamicSourceId: z.string().min(1).max(100).optional(),
      key: z.string().optional(),
      providerId: z.string().optional(),
      folderId: z.string().optional(),
      datasetId: z.string().optional(),
      datasetVersion: z.number().int().positive().optional(),
      valueColumn: z.string().optional(),
      labelColumn: z.string().optional(),
      lookupFieldId: z.string().optional(),
      matchColumn: z.string().optional(),
      matchOperator: matchOperatorSchema.optional(),
      resultColumn: z.string().optional(),
      statementId: z.string().min(1).max(200).optional(),
      instanceId: z.string().optional(),
      structId: z.string().optional(),
    })
    .default({ kind: "literal" }),
  choices: z.array(choiceSchema).default([]),
  choicePresentation: z.enum(["select", "thumbnails"]).optional(),
  validation: validationSettingsSchema.default({}),
  format: formatSettingsSchema.default({
    locale: "en-US",
    prefix: "",
    suffix: "",
    uppercase: false,
  }),
  visibleWhen: conditionSchema.optional(),
  visibilityAction: z.enum(["show", "hide"]).default("show"),
  price: z.number().min(0).default(0),
  proofName: z.boolean().default(false),
  autoGenerated: z.boolean().default(false),
  image: z
    .object({
      maxBytes: z.number().positive().max(20000000).default(10000000),
      minDpi: z.number().nonnegative().default(0),
      tools: z.array(z.enum(imageTools)).default([...imageTools]),
      fixedAspect: z.boolean().default(false),
      aspect: z.number().positive().default(1),
      allowUpload: z.boolean().default(true),
    })
    .default({
      maxBytes: 10000000,
      minDpi: 0,
      tools: [...imageTools],
      fixedAspect: false,
      aspect: 1,
      allowUpload: true,
    }),
  map: mapSettingsSchema.optional(),
  qr: z
    .object({
      disabledParts: z.array(z.string()).max(50).optional(),
      locationMode: z.enum(["auto", "address", "coordinates"]).optional(),
      kind: z
        .enum([
          "text",
          "website",
          "email",
          "phone",
          "sms",
          "location",
          "contact",
          "wifi",
          "event",
        ])
        .default("website"),
      mapping: z.record(z.string(), z.string()).default({}),
      /** Editable defaults filled from other form fields. */
      defaults: z.record(z.string(), z.string()).optional(),
      /** Fixed answers for QR parts that are neither asked nor mapped. */
      fixed: z.record(z.string(), z.string().max(2000)).optional(),
      foreground: z.string().default("#182f32"),
      background: z.string().default("transparent"),
    })
    .optional(),
  presets: z
    .array(
      z.object({
        id: z.string().min(1).max(200),
        label: z.string(),
        match: z
          .object({
            field: z.string().min(1),
            op: matchOperatorSchema,
            value: z.string(),
          })
          .strict()
          .optional(),
        values: z
          .record(z.string(), z.any())
          .refine(
            (v) => Object.keys(v).length <= 1000,
            "Preset has too many targets",
          ),
      }),
    )
    .max(1000)
    .default([]),
  legacy: z.record(z.string(), z.any()).optional(),
});
export type Field = z.infer<typeof fieldSchema>;
export const groupSchema = z.object({
  id: z.string(),
  name: z.string(),
  column: z.number().int().min(1).max(3).default(1),
  order: z.number().default(0),
  visible: z.boolean().default(true),
  note: z.string().default(""),
  instanceId: z.string().optional(),
});
export const textRoleSchema = z.enum([
  "title",
  "heading",
  "body",
  "label",
  "caption",
]);
export const typographyOverrideSchema = z
  .object({
    fontFamily: z.string().min(1).max(200),
    fontFaceId: z.string().nullable(),
    fontSize: z.number().positive().max(14400),
    fontWeight: z.number().min(1).max(1000),
    color: z.string().min(1).max(100),
    lineHeight: z.number().positive().max(100),
    align: z.enum(["left", "center", "right"]),
  })
  .partial()
  .strict();
export const tabIntervalSchema = z.number().min(0.01).max(14400);
export const typographySchema = z.object({
  fontFamily: z.string().min(1).max(200).default("Inter"),
  fontFaceId: z.string().nullable().default(null),
  fontSize: z.number().positive().max(14400).default(12),
  fontWeight: z.number().min(1).max(1000).default(400),
  color: z.string().min(1).max(100).default("#183c3c"),
  lineHeight: z.number().positive().max(100).default(1.35),
  align: z.enum(["left", "center", "right"]).default("left"),
});
export type Typography = z.infer<typeof typographySchema>;
export type TypographyOverrides = z.infer<typeof typographyOverrideSchema>;
export const styleSchema = typographySchema.omit({ fontFaceId: true }).extend({
  id: z.string(),
  name: z.string().min(1).max(200),
  fontFaceId: z.string().nullable().optional(),
  role: textRoleSchema.optional(),
  inheritTypography: z.boolean().default(false),
  overrides: typographyOverrideSchema.default({}),
});
export const defaultTextStyles = () =>
  (["title", "heading", "body", "label", "caption"] as const).map(
    (role, index) =>
      styleSchema.parse({
        id: `text_role_${role}`,
        name: `${role[0].toUpperCase() + role.slice(1)} Text`,
        role,
        inheritTypography: true,
        overrides: {
          fontSize: [28, 20, 12, 10, 9][index],
          fontWeight:
            role === "title" || role === "heading" || role === "label"
              ? 700
              : 400,
        },
      }),
  );
export const documentSettingsSchema = z.object({
  defaultStyleId: z.string().optional(),
  background: z.string().nullable().default("#ffffff"),
  typography: typographySchema.default(typographySchema.parse({})),
});
export const placedPdfSchema = z
  .object({
    version: z.literal(1),
    assetId: z.string().regex(/^asset_[a-zA-Z0-9_-]+$/),
    data: z
      .string()
      .max(12000000)
      .regex(/^[A-Za-z0-9+/]*={0,2}$/),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    width: z.number().positive().max(1000000),
    height: z.number().positive().max(1000000),
  })
  .strict()
  .superRefine((value, ctx) => {
    try {
      const bytes = bytesFromBase64(value.data);
      if (
        bytes.length > 8000000 ||
        new TextDecoder().decode(bytes.subarray(0, 5)) !== "%PDF-" ||
        bytesHash(bytes) !== value.sha256
      )
        ctx.addIssue({
          code: "custom",
          message: "Placed PDF integrity check failed",
        });
    } catch {
      ctx.addIssue({
        code: "custom",
        message: "Placed PDF integrity check failed",
      });
    }
  });
export type PlacedPdf = z.infer<typeof placedPdfSchema>;
export const layoutMarginsSchema = z.object({
  top: z.number().min(0).max(14400).default(0),
  right: z.number().min(0).max(14400).default(0),
  bottom: z.number().min(0).max(14400).default(0),
  left: z.number().min(0).max(14400).default(0),
});
export const nodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum([
    "text",
    "richtext",
    "image",
    "qr",
    "rect",
    "ellipse",
    "line",
    "calendar",
    "map",
    "path",
  ]),
  fieldId: z.string().optional(),
  placedPdf: placedPdfSchema.optional(),
  content: z.string().default(""),
  textTemplate: textTemplateSchema.optional(),
  textCalculation: createCalculationSchema(conditionSchema).optional(),
  x: z.number().min(-14400).max(14400),
  y: z.number().min(-14400).max(14400),
  width: z.number().positive().max(14400),
  height: z.number().positive().max(14400),
  rotation: z.number().default(0),
  opacity: z.number().min(0).max(1).default(1),
  locked: z.boolean().default(false),
  hidden: z.boolean().default(false),
  styleId: z.string().optional(),
  typographyMode: z.enum(["direct", "inherit"]).default("direct"),
  typographyOverrides: typographyOverrideSchema.default({}),
  fontFamily: z.string().default("Inter"),
  fontFaceId: z.string().optional(),
  fontSize: z.number().positive().max(14400).default(12),
  fontWeight: z.number().default(400),
  color: z.string().default("#183c3c"),
  fill: z.string().default("transparent"),
  stroke: z.string().default("transparent"),
  strokeWidth: z.number().nonnegative().default(1),
  radius: z.number().nonnegative().default(0),
  pathViewport: z
    .object({
      x: z.number().min(-1000000).max(1000000),
      y: z.number().min(-1000000).max(1000000),
      width: z.number().min(0.000001).max(1000000),
      height: z.number().min(0.000001).max(1000000),
      yAxis: z.enum(["down", "up"]),
    })
    .strict()
    .optional(),
  path: z
    .string()
    .max(200000)
    .regex(/^[MLCQZHVSATmlcqzhvsat0-9eE.,+\s-]*$/, "Invalid vector path")
    .refine(
      (path) =>
        (path.match(/[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi) || []).every(
          (value) =>
            Number.isFinite(Number(value)) &&
            Math.abs(Number(value)) <= 1000000,
        ),
      "Vector coordinates must be finite and bounded",
    )
    .refine(
      (path) => !path || !vectorPathError(path),
      "Invalid vector path syntax",
    )
    .optional(),
  pathWidth: z.number().positive().max(14400).optional(),
  pathHeight: z.number().positive().max(14400).optional(),
  fillRule: z.enum(["nonzero", "evenodd"]).default("nonzero"),
  align: z.enum(["left", "center", "right"]).default("left"),
  lineHeight: z.number().positive().default(1.35),
  verticalAlign: z
    .enum(["top", "middle", "bottom", "bottom-baseline"])
    .optional(),
  baselineMode: z.enum(["em", "ascent"]).optional(),
  textFeatures: textFeaturesSchema.optional(),
  tabInterval: tabIntervalSchema.optional(),
  textBaselineShift: z.number().min(-500).max(500).optional(),
  clipImageToFrame: z.boolean().optional(),
  imagePlacement: z
    .object({
      scaleX: z.number().min(0.01).max(100),
      scaleY: z.number().min(0.01).max(100),
      offsetX: z.number().min(-14400).max(14400),
      offsetY: z.number().min(-14400).max(14400),
    })
    .strict()
    .optional(),
  textHeightMode: z.enum(["line-box", "font-box"]).optional(),
  copyfit: copyfitSchema.optional(),
  allowGlyphOverhang: z.boolean().optional(),
  sourcePaints: z
    .object({
      color: importedPaintSchema.optional(),
      fill: importedPaintSchema.optional(),
      stroke: importedPaintSchema.optional(),
    })
    .strict()
    .optional(),
  placement: z.enum(["fixed", "flow"]).default("fixed"),
  layoutGroupId: z.string().min(1).max(100).optional(),
  layoutOrder: z.number().int().min(-10000).max(10000).optional(),
  layoutMargins: layoutMarginsSchema.optional(),
  contentHeight: z.boolean().optional(),
  flowRegionId: z.string().optional(),
  flowOrder: z.number().int().min(-10000).max(10000).default(0),
  flowGrow: z.boolean().default(true),
  flowPush: z.boolean().default(true),
  flowGap: z.number().min(0).max(14400).optional(),
  minHeight: z.number().min(0).max(14400).default(0),
  maxHeight: z.number().positive().max(14400).default(14400),
  flowWidth: z.enum(["region", "node"]).default("region"),
  overflow: z.enum(["clip", "shrink", "continue"]).default("clip"),
  anchor: z
    .enum(["top-left", "top-right", "bottom-left", "bottom-right", "center"])
    .default("top-left"),
  stretch: z.enum(["none", "horizontal", "vertical", "both"]).default("none"),
  visibleWhen: conditionSchema.optional(),
});
export type DesignNode = z.infer<typeof nodeSchema>;
export const documentFrameSchema = z.object({
  name: z.string().min(1).max(200),
  path: nodeSchema.shape.path.unwrap().min(1),
  width: z.number().positive().max(14400),
  height: z.number().positive().max(14400),
  fillRule: z.enum(["nonzero", "evenodd"]).default("evenodd"),
});
export type DocumentFrame = z.infer<typeof documentFrameSchema>;
export const flowRegionSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(100)
    .refine((id) => id !== "body", "body is reserved for the page body"),
  name: z.string().min(1).max(200),
  x: z.number().min(0).max(14400),
  y: z.number().min(0).max(14400),
  width: z.number().positive().max(14400),
  height: z.number().positive().max(14400),
  gap: z.number().min(0).max(14400).default(8),
  nextRegionId: z.string().nullable().optional(),
});
export type FlowRegion = z.infer<typeof flowRegionSchema>;
const validGroupBounds = (value: {
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
}) =>
  (value.minWidth === undefined ||
    value.maxWidth === undefined ||
    value.minWidth <= value.maxWidth) &&
  (value.minHeight === undefined ||
    value.maxHeight === undefined ||
    value.minHeight <= value.maxHeight);
export const layoutGroupBoundsSchema = z
  .object({
    minWidth: z.number().min(0).max(14400).optional(),
    maxWidth: z.number().positive().max(14400).optional(),
    minHeight: z.number().min(0).max(14400).optional(),
    maxHeight: z.number().positive().max(14400).optional(),
  })
  .strict()
  .refine(
    validGroupBounds,
    "Group minimum dimensions cannot exceed maximum dimensions",
  );
export const layoutGroupSchema = z
  .object({
    id: z.string().min(1).max(100),
    name: z.string().min(1).max(200),
    /** Locked together for designer movement; unlock to edit individual members. */
    moveTogether: z.boolean().optional(),
    textAlign: z.enum(["left", "center", "right"]).optional(),
    offsetX: z.number().min(-14400).max(14400).optional(),
    offsetY: z.number().min(-14400).max(14400).optional(),
    parentId: z.string().min(1).max(100).optional(),
    direction: z
      .enum(["vertical", "horizontal", "overlay"])
      .default("vertical"),
    x: z.number().min(-14400).max(14400).default(0),
    y: z.number().min(-14400).max(14400).default(0),
    width: z.number().positive().max(14400).optional(),
    height: z.number().positive().max(14400).optional(),
    ...layoutGroupBoundsSchema.shape,
    clipContent: z.boolean().optional(),
    order: z.number().int().min(-10000).max(10000).default(0),
    margins: layoutMarginsSchema.default({
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    }),
    gap: z.number().min(0).max(14400).default(0),
    align: z.enum(["start", "center", "end"]).default("start"),
    anchorX: z.enum(["start", "center", "end"]).default("start"),
    anchorY: z.enum(["start", "center", "end"]).default("start"),
    visibleWhen: conditionSchema.optional(),
  })
  .refine(
    validGroupBounds,
    "Group minimum dimensions cannot exceed maximum dimensions",
  );
export type LayoutGroup = z.infer<typeof layoutGroupSchema>;
export const pageSchema = z.object({
  id: z.string(),
  name: z.string(),
  frame: documentFrameSchema.optional(),
  baseDocumentNodeId: z.string().min(1).max(200).optional(),
  artBox: pdfArtBoxSchema.optional(),
  width: z.number().positive().max(14400).nullable().default(612),
  height: z.number().positive().max(14400).nullable().default(792),
  background: z.string().nullable().default("#ffffff"),
  backgroundMode: z.enum(["direct", "inherit"]).default("direct"),
  margin: z.number().nonnegative().default(36),
  nodes: z.array(nodeSchema).max(2000),
  flowRegions: z.array(flowRegionSchema).max(32).default([]),
  layoutGroups: z.array(layoutGroupSchema).max(2000).optional(),
  production: productionSchema.default({ bleed: 0, slug: 0, cropMarks: false }),
});
export type Page = z.infer<typeof pageSchema>;
export const outputSchema = z
  .object({
    outputPreset: z
      .object({
        id: z.string(),
        version: z.number().int().positive(),
        name: z.string(),
      })
      .optional(),
    role: z.enum(["preview", "proof", "high-res", "print"]).optional(),
    rasterize: z.boolean().optional(),
    productionProfile: z
      .object({
        id: z.string(),
        version: z.number().int().positive(),
        name: z.string(),
      })
      .optional(),
    sheetProfile: z
      .object({
        id: z.string(),
        version: z.number().int().positive(),
        name: z.string(),
      })
      .optional(),
    id: z.string(),
    name: z.string(),
    format: z.enum(["pdf", "svg", "png", "jpeg", "tiff"]),
    colorSpace: z.enum(["rgb", "cmyk"]).default("rgb"),
    preserveImportedPdf: z.boolean().optional(),
    preserveImportedPaint: z.boolean().optional(),
    absoluteTextPositions: z.boolean().optional(),
    rgbPrecision: z.number().int().min(3).max(6).optional(),
    includeProduction: z.boolean().optional(),
    production: productionSchema.optional(),
    artBox: pdfArtBoxSchema.optional(),
    printProfile: printProfileSchema.optional(),
    dpi: z.number().int().min(36).max(1200).default(300),
    quality: z.number().int().min(1).max(100).default(95),
    watermark: z.string().default(""),
    minDpi: z.number().nonnegative().default(0),
    lowDpiPolicy: z.enum(["warn", "block"]).default("warn"),
    allowView: z.boolean().default(true),
    allowDownload: z.boolean().default(true),
    imposition: impositionSchema.default(impositionSchema.parse({})),
  })
  .superRefine((output, ctx) => {
    if (
      (output.role === "preview" || output.role === "proof") &&
      output.imposition.enabled
    )
      ctx.addIssue({
        code: "custom",
        message: "Preview and proof outputs use individual product pages",
      });
    if (
      (output.rasterize ||
        output.role === "preview" ||
        output.role === "proof") &&
      output.colorSpace !== "rgb"
    )
      ctx.addIssue({
        code: "custom",
        message: "Raster proof output requires RGB",
      });
    if (output.role === "proof" && output.format === "svg")
      ctx.addIssue({
        code: "custom",
        message: "Proof downloads require a raster format or raster PDF",
      });
  });
export type OutputProfile = z.infer<typeof outputSchema>;
export const resourceKindSchema = z.enum([
  "form",
  "rule",
  "preset",
  "page-size",
  "production",
  "output",
  "dataset",
  "style",
  "sheet",
  "validation",
  "format",
  "font",
]);
export type ResourceKind = z.infer<typeof resourceKindSchema>;
export const resourceSnapshotSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  kind: resourceKindSchema,
  name: z.string(),
  version: z.number().int().positive(),
  value: z.any(),
});
export const dynamicParameterSchema = z
  .object({
    key: z
      .string()
      .min(1)
      .max(100)
      .regex(/^[^{}\u0000-\u001f\u007f]+$/)
      .refine(
        (key) =>
          key === key.trim() &&
          !["__proto__", "constructor", "prototype"].includes(key),
      ),
    label: z.string().min(1).max(100),
    type: z.enum([
      "text",
      "number",
      "email",
      "phone",
      "dropdown",
      "radio",
      "checkbox",
    ]),
    help: z.string().max(1000).default(""),
    whitespace: z.enum(["preserve", "trim"]).optional(),
    required: z.boolean().default(true),
    default: z
      .union([z.string().max(1000), z.number(), z.boolean(), z.null()])
      .default(""),
    choices: z
      .array(
        z
          .object({
            value: z.string().min(1).max(1000),
            label: z.string().max(1000),
          })
          .strict(),
      )
      .max(1000)
      .default([]),
  })
  .strict()
  .superRefine((parameter, ctx) => {
    if (
      new Set(parameter.choices.map((choice) => choice.value)).size !==
        parameter.choices.length ||
      (parameter.type === "checkbox" &&
        (parameter.choices.length ||
          (typeof parameter.default !== "boolean" && parameter.default !== "")))
    )
      ctx.addIssue({
        code: "custom",
        message:
          "Parameter choices must be unique; checkbox parameters are boolean",
      });
  });
export const dynamicSourceSchema = z
  .object({
    /** Nonsecret connector declaration; credentials and approval remain server-owned. */
    integration: integrationDataSourceSchema.optional(),
    id: z.string().min(1).max(100),
    name: z.string().min(1).max(100),
    allowSkip: z.boolean().optional(),
    parameters: z.array(dynamicParameterSchema).max(100),
  })
  .strict()
  .refine(
    (source) =>
      new Set(source.parameters.map((parameter) => parameter.key)).size ===
      source.parameters.length,
    "Parameter keys must be unique",
  );
export type DynamicSource = z.infer<typeof dynamicSourceSchema>;
export type SourceParameters = Record<
  string,
  Record<string, string | number | boolean | null> | null
>;
export const projectSchema = z.object({
  dynamicSources: z.array(dynamicSourceSchema).max(100).optional(),
  schemaVersion: z.literal(1),
  id: z.string().min(1),
  tenantId: z.string().min(1).default("demo"),
  name: z.string().min(1).max(100),
  description: z.string().max(500).default(""),
  instructions: z.string().default(""),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  usedAt: z.string().datetime().optional(),
  revision: z.number().int().nonnegative().default(0),
  fields: z.array(fieldSchema).max(1000),
  groups: z.array(groupSchema).default([]),
  tabs: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        order: z.number(),
        groupIds: z.array(z.string()),
      }),
    )
    .default([]),
  triggers: z
    .array(
      z.object({
        id: z.string(),
        sourceId: z.string(),
        op: matchOperatorSchema.optional(),
        value: z.any(),
        show: z.array(z.string()).default([]),
        hide: z.array(z.string()).default([]),
        clear: z.array(z.string()).default([]),
        exclude: z.record(z.string(), z.array(z.string())).default({}),
      }),
    )
    .default([]),
  parts: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        sku: z.string(),
        instructions: z.string().default(""),
        overrides: z.record(z.string(), z.any()).default({}),
        systemValues: z.record(z.string(), z.any()).default({}),
        dynamicSourcePolicies: z
          .record(z.string().min(1).max(100), z.enum(["required", "optional"]))
          .optional(),
      }),
    )
    .default([]),
  pages: z.array(pageSchema).min(1).max(100),
  styles: z.array(styleSchema).default([]),
  resourceSnapshots: z.array(resourceSnapshotSchema).max(100).default([]),
  assetLibrary: z
    .array(z.object({ assetId: z.string().min(1).max(500) }).strict())
    .max(1000)
    .optional(),
  documentSettings: documentSettingsSchema.default(
    documentSettingsSchema.parse({}),
  ),
  fontManifest: z.array(fontFaceSchema).max(100).default([]),
  outputs: z.array(outputSchema).min(1).max(30),
  archivedOutputs: z.array(outputSchema).max(100).optional(),
  datasets: z.array(datasetSchema).max(100).default([]),
  forms: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        fields: z.array(fieldSchema),
        allowCreate: z.boolean().default(true),
        profileSource: formProfileSourceSchema.optional(),
        profileLibrary: z
          .object({
            id: z.string().min(1).max(200),
            version: z.number().int().positive(),
            inputIds: z
              .record(z.string().min(1).max(200), z.string().min(1).max(200))
              .refine((value) => Object.keys(value).length <= 100)
              .optional(),
          })
          .strict()
          .optional(),
      }),
    )
    .default([]),
  formInstances: z
    .array(
      z.object({
        id: z.string(),
        formId: z.string(),
        label: z.string(),
        mapping: z.record(z.string(), z.string()),
        mappedOnly: z.boolean().optional(),
        profileOnly: z.boolean().optional(),
        inputOnly: z.array(z.string()).max(100).optional(),
        /** Where the profile switcher shows: "top", a section id, or automatic. */
        placement: z.string().max(200).optional(),
      }),
    )
    .default([]),
  settings: z
    .object({
      formSectionLayout: z.enum(["aligned", "stacked"]).optional(),
      bleedPreview: z
        .object({ enabled: z.boolean(), defaultVisible: z.boolean() })
        .optional(),
      comments: z.boolean().default(true),
      lockProofName: z.boolean().default(false),
      overflowWarning: z.boolean().default(true),
      overflowMessage: z
        .string()
        .default("Some content extends beyond its frame."),
      header: z.string().default(""),
      footer: z.string().default(""),
      bandHeight: z.number().nonnegative().max(144).default(24),
      headerHeight: z.number().nonnegative().max(144).optional(),
      footerHeight: z.number().nonnegative().max(144).optional(),
      headerStyleId: z.string().optional(),
      footerStyleId: z.string().optional(),
      metadata: z.record(z.string(), z.string()).default({}),
    })
    .default({
      comments: true,
      lockProofName: false,
      overflowWarning: true,
      overflowMessage: "Some content extends beyond its frame.",
      header: "",
      footer: "",
      bandHeight: 24,
      metadata: {},
    }),
  legacy: z.record(z.string(), z.any()).optional(),
});
export type Project = z.infer<typeof projectSchema>;
export type Part = Project["parts"][number];
export type Trigger = Project["triggers"][number];
export type TextStyle = z.infer<typeof styleSchema>;
export const uid = (prefix = "id") => {
  if (typeof globalThis.crypto?.randomUUID === "function")
    return `${prefix}_${globalThis.crypto.randomUUID()}`;
  const bytes = globalThis.crypto?.getRandomValues(new Uint8Array(16));
  if (!bytes) throw new Error("Secure random generation is unavailable");
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (n) => n.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${prefix}_${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
export const newField = (type: Field["type"] = "text", count = 0): Field =>
  fieldSchema.parse({
    id: uid("field"),
    key: `Field${count + 1}`,
    label: `Untitled ${type} field`,
    type,
    order: count,
  });
export const newNode = (type: DesignNode["type"] = "text"): DesignNode =>
  nodeSchema.parse({
    id: uid("node"),
    name: type === "text" ? "Text layer" : `${type} layer`,
    type,
    x: 36,
    y: 36,
    width: type === "calendar" ? 240 : type === "qr" ? 96 : 180,
    height: type === "calendar" ? 180 : type === "text" ? 40 : 96,
    content: type === "text" ? "Your text here" : "",
    ...(type === "path"
      ? {
          path: "M0 0H180V96H0Z",
          pathWidth: 180,
          pathHeight: 96,
          fill: "#638875",
          stroke: "transparent",
          strokeWidth: 0,
        }
      : {}),
  });
export const newPage = (): Page =>
  pageSchema.parse({ id: uid("page"), name: "New page", nodes: [] });
export function parseProject(value: unknown): Project {
  const p = projectSchema.parse(value);
  p.datasets = p.datasets.map(orderedDataset);
  const occupied = new Set(
    [
      ...p.fields,
      ...p.pages,
      ...p.pages.flatMap((pg) => pg.nodes),
      ...p.styles,
      ...p.parts,
      ...p.outputs,
      ...p.groups,
      ...p.tabs,
      ...p.datasets.filter(
        (d, i) => p.datasets.findIndex((other) => other.id === d.id) === i,
      ),
      ...p.forms,
      ...p.formInstances,
    ].map((item) => item.id),
  );
  for (const builtin of defaultTextStyles()) {
    const named = p.styles.find(
      (s) => !s.role && s.name.toLowerCase() === builtin.name.toLowerCase(),
    );
    if (named && !p.styles.some((s) => s.role === builtin.role))
      named.role = builtin.role;
  }
  for (const builtin of defaultTextStyles())
    if (!p.styles.some((s) => s.role === builtin.role)) {
      let id = builtin.id,
        index = 0;
      while (occupied.has(id)) id = `${builtin.id}_${++index}`;
      p.styles.push({ ...builtin, id });
      occupied.add(id);
    }
  // Upgrade older single-version bindings without resolving ambiguous data.
  for (const source of [
    ...p.fields.map((f) => f.source),
    ...p.parts.flatMap((part) =>
      Object.values(part.overrides).flatMap((patch) =>
        patch.source ? [patch.source] : [],
      ),
    ),
  ]) {
    if (
      ["dataset", "lookup", "statement"].includes(source.kind) &&
      !source.datasetVersion
    ) {
      const matches = p.datasets.filter((d) => d.id === source.datasetId);
      if (matches.length === 1) source.datasetVersion = matches[0].version;
    }
  }
  const legacyPresentation = (control: unknown) =>
    control === "ImageBankListView"
      ? ("thumbnails" as const)
      : control === "ImageBank" || control === "ImageList"
        ? ("select" as const)
        : undefined;
  // Interpret older native snapshots once; explicit presentation is the migration marker.
  for (const field of p.fields) {
    if (!["image", "image-list"].includes(field.type)) continue;
    if (field.type === "image-list" && field.choicePresentation === undefined) {
      const presentation = legacyPresentation(field.legacy?.CONTROL_TYPE);
      if (presentation) {
        field.choicePresentation = presentation;
        for (const part of p.parts) {
          const patch = part.overrides[field.id];
          if (
            !patch ||
            patch.choicePresentation !== undefined ||
            (patch.type || field.type) !== "image-list"
          )
            continue;
          const own = legacyPresentation(patch.legacy?.CONTROL_TYPE);
          if (own && own !== presentation) patch.choicePresentation = own;
        }
      }
    }
    // Earlier adapter versions represented legacy DAM groups as generic asset keys.
    const upgrade = (source: Field["source"] | undefined, group: unknown) => {
      if (
        source?.kind === "asset" &&
        group != null &&
        source.key &&
        /^\d+$/.test(source.key)
      ) {
        source.kind = "dam";
        source.providerId = "legacy";
        source.folderId = source.key;
        delete source.key;
      }
    };
    upgrade(field.source, field.legacy?.DAM_ASSET_GROUP_ID);
    for (const part of p.parts) {
      const patch = part.overrides[field.id];
      if (patch?.source)
        upgrade(
          patch.source,
          patch.legacy?.DAM_ASSET_GROUP_ID ?? field.legacy?.DAM_ASSET_GROUP_ID,
        );
    }
  }
  assertProject(p);
  return p;
}
export function safePattern(pattern: string): RegExp {
  if (
    pattern.length > 256 ||
    /\\[1-9]|\(\?[=!<]|\([^)]*[+*][^)]*\)[+*{]|\([^)]*\|[^)]*\)[+*{]/.test(
      pattern,
    )
  )
    throw new Error("Pattern uses unsupported or unsafe constructs");
  return new RegExp(pattern, "u");
}
export function assertProject(p: Project): void {
  const outputRoles = p.outputs.map((o) => o.role).filter(Boolean);
  if (new Set(outputRoles).size !== outputRoles.length)
    throw new Error("Each output stage can be assigned once");
  if (outputRoles.length && p.outputs.length > 4)
    throw new Error("Projects support up to four active output profiles");
  const dynamicIds = new Set(
    (p.dynamicSources || []).map((source) => source.id),
  );
  if (dynamicIds.size !== (p.dynamicSources || []).length)
    throw new Error("Duplicate dynamic source");
  for (const part of p.parts)
    if (
      Object.keys(part.dynamicSourcePolicies || {}).some(
        (id) => !dynamicIds.has(id),
      )
    )
      throw new Error("Unknown part dynamic source policy");
  for (const field of [
    ...p.fields,
    ...p.forms.flatMap((form) => form.fields),
    ...p.parts.flatMap((part) => Object.values(part.overrides)),
  ])
    if (
      field.source?.dynamicSourceId &&
      (field.source.kind !== "dynamic" ||
        !dynamicIds.has(field.source.dynamicSourceId))
    )
      throw new Error("Unknown dynamic parameter source");
  if (p.fontManifest.reduce((bytes, f) => bytes + f.bytes, 0) > 32000000)
    throw new Error("Managed font manifest exceeds 32 MB");
  const placedPdfs = p.pages.flatMap((page) =>
    page.nodes.filter((node) => node.placedPdf),
  );
  if (
    p.pages.some((page) =>
      page.nodes.some((node) => node.pathViewport && node.type !== "path"),
    )
  )
    throw new Error("Vector viewport requires a path layer");
  if (placedPdfs.some((node) => node.type !== "image"))
    throw new Error("Placed PDF artwork requires an image layer");
  if (
    p.pages.some((page) =>
      page.nodes.some((node) => node.imagePlacement && node.type !== "image"),
    )
  )
    throw new Error("Image placement requires an image layer");
  if (
    placedPdfs.reduce(
      (size, node) => size + node.placedPdf!.data.length * 0.75,
      0,
    ) > 32000000
  )
    throw new Error("Placed PDF artwork exceeds 32 MB");
  const fontIds = new Set(p.fontManifest.map((f) => f.id));
  if (fontIds.size !== p.fontManifest.length)
    throw new Error("Duplicate managed font face");
  for (const id of [p.settings.headerStyleId, p.settings.footerStyleId])
    if (id && !p.styles.some((s) => s.id === id))
      throw new Error(`Unknown band text style: ${id}`);
  if (
    p.documentSettings.defaultStyleId &&
    !p.styles.some((s) => s.id === p.documentSettings.defaultStyleId)
  )
    throw new Error("Unknown default text style");
  const roles = p.styles.filter((s) => s.role).map((s) => s.role);
  if (new Set(roles).size !== roles.length)
    throw new Error("Duplicate built-in text role");
  const typographyRefs = [
    p.documentSettings.typography,
    ...p.styles.map((s) => s.overrides),
    ...p.pages.flatMap((pg) => pg.nodes.map((n) => n.typographyOverrides)),
  ];
  for (const properties of typographyRefs)
    if (properties.fontFaceId && !fontIds.has(properties.fontFaceId))
      throw new Error(
        `Missing managed font snapshot: ${properties.fontFaceId}`,
      );
  for (const node of p.pages.flatMap((pg) => pg.nodes))
    if (node.styleId && !p.styles.some((s) => s.id === node.styleId))
      throw new Error(`Unknown text style: ${node.styleId}`);
  for (const item of [...p.styles, ...p.pages.flatMap((page) => page.nodes)])
    if (item.fontFaceId && !fontIds.has(item.fontFaceId))
      throw new Error(`Missing managed font snapshot: ${item.fontFaceId}`);
  for (const page of p.pages) {
    assertLayoutGroups(page);
    const regions = page.flowRegions;
    const regionIds = new Set(regions.map((r) => r.id));
    if (regionIds.size !== regions.length)
      throw new Error("Duplicate flow region identity");
    const next = new Map(
      regions.map((r, i) => [
        r.id,
        r.nextRegionId === undefined ? regions[i + 1]?.id : r.nextRegionId,
      ]),
    );
    for (const region of regions) {
      if (next.get(region.id) && !regionIds.has(next.get(region.id)!))
        throw new Error("Unknown next flow region");
      const path = new Set<string>();
      let id: string | null | undefined = region.id;
      while (id) {
        if (path.has(id)) throw new Error("Flow region cycle");
        path.add(id);
        id = next.get(id);
      }
      if (
        (page.width !== null && region.x + region.width > page.width) ||
        (page.height !== null && region.y + region.height > page.height)
      )
        throw new Error("Flow region exceeds logical page bounds");
    }
    for (const node of page.nodes) {
      if (node.minHeight > node.maxHeight)
        throw new Error("Flow minimum height exceeds maximum height");
      if (
        node.flowRegionId &&
        node.flowRegionId !== "body" &&
        !regionIds.has(node.flowRegionId)
      )
        throw new Error("Unknown flow region");
    }
  }
  const ids = new Set<string>();
  for (const item of [
    ...p.fields,
    ...p.pages,
    ...p.pages.flatMap((x) => x.nodes),
    ...p.pages.flatMap((x) => x.layoutGroups || []),
    ...p.groups,
    ...p.tabs,
    ...p.parts,
    ...p.outputs,
    ...p.datasets.filter(
      (d, i) => p.datasets.findIndex((other) => other.id === d.id) === i,
    ),
    ...p.styles,
    ...p.formInstances,
    ...p.forms,
  ]) {
    if (ids.has(item.id)) throw new Error(`Duplicate identity: ${item.id}`);
    ids.add(item.id);
  }
  const datasetVersions = new Set<string>();
  for (const dataset of p.datasets) {
    const key = JSON.stringify([dataset.id, dataset.version]);
    if (datasetVersions.has(key)) throw new Error("Duplicate dataset version");
    datasetVersions.add(key);
    assertDataset(dataset);
  }
  const fields = new Map(p.fields.map((f) => [f.id, f]));
  const presetEdges = presetDependencies(p.fields);
  const keys = new Set<string>();
  const refs = (
    c: Condition,
    depth = 0,
    budget = { n: 0 },
    scope = fields,
  ): string[] => {
    if (depth > 8 || ++budget.n > 100)
      throw new Error("Condition exceeds complexity limit");
    const variants = Number(!!c.all) + Number(!!c.any) + Number(!!c.field);
    if (variants !== 1)
      throw new Error(
        "Condition must contain exactly one field, ALL or ANY expression",
      );
    if (c.all || c.any) {
      const children = c.all || c.any!;
      if (!children.length) throw new Error("Empty condition group");
      return children.flatMap((x) => refs(x, depth + 1, budget, scope));
    }
    if (!scope.has(c.field!))
      throw new Error(`Unknown conditional field: ${c.field}`);
    if (!c.op) throw new Error("Condition operator required");
    if (c.op === "matches") safePattern(String(c.value));
    return [c.field!];
  };
  const calculationRefs = (f: Field, scope = fields): string[] => {
    if (!f.calculation) return [];
    if (f.source.kind !== "literal" || f.autoGenerated)
      throw new Error(
        "Calculated variables require a literal source without automatic generation",
      );
    for (const condition of calculationConditions(f.calculation))
      refs(condition, 0, { n: 0 }, scope);
    const references = calculationDependencies(f.calculation);
    for (const reference of references)
      if (!scope.has(reference))
        throw new Error(`Unknown calculation field: ${reference}`);
    return references;
  };
  const sourceRefs = (f: Field): string[] => {
    if (
      f.source.kind === "dam" &&
      (!["image", "image-list"].includes(f.type) ||
        !/^[a-zA-Z0-9_-]{1,80}$/.test(f.source.providerId || "") ||
        !f.source.folderId?.trim() ||
        f.source.folderId.length > 200 ||
        /[\u0000-\u001f\u007f]/.test(f.source.folderId))
    )
      throw new Error(
        "DAM sources require an image control, provider and root folder",
      );
    if (f.source.kind === "statement") {
      if (!f.source.datasetId) return [];
      const options = statementOptions(p, f);
      if (
        f.source.statementId &&
        !options.some((option) => option.id === f.source.statementId)
      )
        throw new Error(
          "Default statement is unavailable in the configured group version",
        );
      return [];
    }
    if (!["dataset", "lookup"].includes(f.source.kind)) return [];
    const matches = p.datasets.filter(
      (d) =>
        d.id === f.source.datasetId &&
        (!f.source.datasetVersion || d.version === f.source.datasetVersion),
    );
    if (matches.length !== 1)
      throw new Error("Dataset version is missing or ambiguous");
    const dataset = matches[0];
    if (dataset.kind === "table")
      for (const column of [
        f.source.valueColumn,
        f.source.labelColumn,
        f.source.matchColumn,
        f.source.resultColumn,
      ])
        if (column && !dataset.columns.some((c) => c.key === column))
          throw new Error(`Unknown dataset column: ${column}`);
    if (f.source.kind === "lookup") {
      if (
        dataset.kind !== "table" ||
        !f.source.lookupFieldId ||
        !fields.has(f.source.lookupFieldId) ||
        !f.source.resultColumn
      )
        throw new Error(
          "Lookup requires a table, selector field and result column",
        );
      const match = f.source.matchColumn || dataset.primaryKey!;
      if (f.source.matchOperator === "matches")
        for (const row of dataset.rows)
          if (row[match] != null) safePattern(String(row[match]));
      const values = dataset.rows
        .map((row) => row[match])
        .filter((v) => v !== null && v !== undefined)
        .map(String);
      if (new Set(values).size !== values.length)
        throw new Error("Lookup match column must be unique");
      return [f.source.lookupFieldId];
    }
    if (dataset.kind === "table") {
      const key =
        f.source.valueColumn || dataset.valueColumn || dataset.primaryKey!;
      const values = dataset.rows.map((row) => row[key]);
      if (
        values.some((v) => v == null || String(v) === "") ||
        new Set(values.map(String)).size !== values.length
      )
        throw new Error("Dataset choice values must be nonempty and unique");
    }
    return [];
  };
  const edges = new Map<string, string[]>();
  for (const f of p.fields) {
    if (f.key) {
      if (keys.has(f.key))
        throw new Error(`Duplicate variable token: ${f.key}`);
      keys.add(f.key);
    }
    if (f.validation.pattern) safePattern(f.validation.pattern);
    edges.set(f.id, [
      ...(presetEdges.get(f.id) || []),
      ...(f.visibleWhen ? refs(f.visibleWhen) : []),
      ...sourceRefs(f),
      ...calculationRefs(f),
    ]);
    if (f.groupId && !p.groups.some((g) => g.id === f.groupId))
      throw new Error(`Unknown group: ${f.groupId}`);
    if (new Set(f.choices.map((c) => c.value)).size !== f.choices.length)
      throw new Error(`Duplicate choices for ${f.label}`);
  }
  const done = new Set<string>(),
    active = new Set<string>();
  const visit = (id: string) => {
    if (active.has(id)) throw new Error("Conditional dependency cycle");
    if (done.has(id)) return;
    active.add(id);
    for (const ref of edges.get(id) || []) visit(ref);
    active.delete(id);
    done.add(id);
  };
  for (const f of p.fields) visit(f.id);
  for (const group of p.pages.flatMap((page) => page.layoutGroups || []))
    if (group.visibleWhen) refs(group.visibleWhen);
  for (const node of p.pages.flatMap((pg) => pg.nodes)) {
    if (node.copyfit && !["text", "richtext"].includes(node.type))
      throw new Error("Copyfit settings require a text layer");
    if (
      node.tabInterval !== undefined &&
      !["text", "richtext"].includes(node.type)
    )
      throw new Error("Tab stops require a text layer");
    for (const pair of node.textFeatures?.pairAdjustments || [])
      if (pair.fontFaceId && !fontIds.has(pair.fontFaceId))
        throw new Error("Unknown kerning pair font snapshot");
    if (node.textHeightMode === "font-box" && node.baselineMode !== "ascent")
      throw new Error("Font-box text uses ascent baselines");
    if (node.textCalculation) {
      if (
        node.fieldId ||
        node.textTemplate ||
        !["text", "richtext"].includes(node.type)
      )
        throw new Error("Text calculations require an unbound text layer");
      for (const id of calculationDependencies(node.textCalculation))
        if (!fields.has(id))
          throw new Error(`Unknown text calculation field: ${id}`);
      for (const when of calculationConditions(node.textCalculation))
        refs(when);
    }
    if (node.textTemplate) {
      if (node.fieldId || !["text", "richtext"].includes(node.type))
        throw new Error("Text templates require an unbound text layer");
      for (const part of node.textTemplate)
        if ("fieldId" in part && !fields.has(part.fieldId))
          throw new Error(`Unknown text template field: ${part.fieldId}`);
    }
    if (node.fieldId && !fields.has(node.fieldId))
      throw new Error(`Unknown layer field: ${node.fieldId}`);
    if (node.visibleWhen) refs(node.visibleWhen);
  }
  for (const part of p.parts) {
    for (const [id, patch] of Object.entries(part.overrides)) {
      if (!fields.has(id)) throw new Error(`Unknown overridden field: ${id}`);
      const effective = fieldSchema.parse({ ...fields.get(id), ...patch, id });
      if (effective.validation.pattern)
        safePattern(effective.validation.pattern);
      if (
        effective.groupId &&
        !p.groups.some((g) => g.id === effective.groupId)
      )
        throw new Error("Unknown override group");
      if (
        effective.source.kind === "dataset" &&
        !p.datasets.some((d) => d.id === effective.source.datasetId)
      )
        throw new Error("Unknown override dataset");
      if (effective.visibleWhen) refs(effective.visibleWhen);
    }
    const partEdges = new Map<string, string[]>();
    const effectivePartFields = p.fields.map((field) =>
      fieldSchema.parse({
        ...field,
        ...part.overrides[field.id],
        id: field.id,
      }),
    );
    const partPresetEdges = presetDependencies(effectivePartFields);
    for (const field of p.fields) {
      const effective = fieldSchema.parse({
        ...field,
        ...part.overrides[field.id],
        id: field.id,
      });
      partEdges.set(field.id, [
        ...(partPresetEdges.get(field.id) || []),
        ...(effective.visibleWhen ? refs(effective.visibleWhen) : []),
        ...sourceRefs(effective),
        ...calculationRefs(effective),
      ]);
    }
    const seen = new Set<string>(),
      path = new Set<string>();
    const check = (id: string) => {
      if (path.has(id)) throw new Error("Part conditional dependency cycle");
      if (seen.has(id)) return;
      path.add(id);
      for (const dep of partEdges.get(id) || []) check(dep);
      path.delete(id);
      seen.add(id);
    };
    p.fields.forEach((f) => check(f.id));
    for (const fieldId of Object.keys(part.systemValues))
      if (!fields.has(fieldId)) throw new Error("Unknown part system variable");
  }
  for (const t of p.triggers) {
    if (t.op === "matches") safePattern(String(t.value));
    if (!fields.has(t.sourceId))
      throw new Error(`Unknown trigger source: ${t.sourceId}`);
    for (const target of [
      ...t.show,
      ...t.hide,
      ...t.clear,
      ...Object.keys(t.exclude),
    ])
      if (!fields.has(target) && !p.groups.some((g) => g.id === target))
        throw new Error(`Unknown trigger target: ${target}`);
  }
  for (const tab of p.tabs)
    for (const id of tab.groupIds)
      if (!p.groups.some((g) => g.id === id))
        throw new Error(`Unknown tab group: ${id}`);
  for (const form of p.forms) {
    if (!form.fields.some((field) => field.calculation)) continue;
    const scope = new Map(form.fields.map((field) => [field.id, field]));
    if (scope.size !== form.fields.length)
      throw new Error("Duplicate form field identity");
    const presetEdges = presetDependencies(form.fields);
    const edges = new Map(
      form.fields.map((field) => [
        field.id,
        [
          ...calculationRefs(field, scope),
          ...(field.visibleWhen
            ? refs(field.visibleWhen, 0, { n: 0 }, scope)
            : []),
          ...(presetEdges.get(field.id) || []),
          ...(field.source.kind === "lookup" && field.source.lookupFieldId
            ? [field.source.lookupFieldId]
            : []),
        ],
      ]),
    );
    const done = new Set<string>(),
      active = new Set<string>();
    const visit = (id: string) => {
      if (!scope.has(id))
        throw new Error(`Unknown form calculation dependency: ${id}`);
      if (active.has(id)) throw new Error("Form calculation dependency cycle");
      if (done.has(id)) return;
      active.add(id);
      for (const dependency of edges.get(id) || []) visit(dependency);
      active.delete(id);
      done.add(id);
    };
    for (const field of form.fields) visit(field.id);
  }
  for (const instance of p.formInstances) {
    const form = p.forms.find((f) => f.id === instance.formId);
    if (!form) throw new Error("Unknown reusable form");
    for (const [struct, target] of Object.entries(instance.mapping))
      if (!form.fields.some((f) => f.id === struct) || !fields.has(target))
        throw new Error("Invalid form instance mapping");
  }
  for (const f of p.fields)
    if (
      f.source.kind === "dataset" &&
      !p.datasets.some((d) => d.id === f.source.datasetId)
    )
      throw new Error(`Unknown dataset: ${f.source.datasetId}`);
}
